import { useEffect, useMemo, useState } from "react";
import AutocompleteInput from "./AutocompleteInput";
import AddressAutocomplete from "./AddressAutocomplete";
import CompagniaField from "./CompagniaField";
import DateInput from "./DateInput";
import Combobox, { DOC_TIPI, DOC_RILASCIATO, COLORI_VEICOLO } from "./Combobox";
import TitoloInput from "./TitoloInput";
import { withCfSesso } from "../lib/cfSesso";
import DecodificaCFButton from "./DecodificaCFButton";
import IspettoratoField from "./IspettoratoField";
import RuoloSinistroField from "./RuoloSinistroField";
import ConducenteFormSection from "./ConducenteFormSection";
import { ValidationHint } from "./ValidationHint";
import { validateCodiceFiscale } from "../lib/validators";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Users, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { CodiceFiscale } from "codice-fiscale-js";
import { isValidTarga } from "../lib/targa";
import SoggettoIvaTabs from "./SoggettoIvaTabs";
import MultiCellulare from "./MultiCellulare";
import LesioniPSCard, { isConducenteFerito } from "./LesioniPSCard";

const LBL = "text-slate-300 text-xs uppercase tracking-wide";
const INPUT_CLS = "mt-1.5 h-9 bg-slate-900/60 border-slate-700";

const EMPTY = {
  soggetto_iva: "NO", cognome: "", nome: "", sesso: "",
  nasc_g: "", nasc_m: "", nasc_a: "",
  comune_nascita: "", comune_nascita_prov: "",
  codice_fiscale: "", professione: "",
  indirizzo: "", numero_civico: "", comune: "", cap: "", provincia: "",
  telefono: "", tel_no_cai: false, email: "", pec: "",
  ruolo: "", assicurazione: "", ispettorato: "", polizza: "",
  scad_g: "", scad_m: "", scad_a: "",
  costruttore: "", modello: "", colore: "", targa: "", data_immatricolazione: "",
  veicolo_estero: "NO", documento: "", numero_doc: "", rilasciato: "", luogo_rilascio_doc: "", data_emissione_doc: "", data_scadenza_doc: "",
  num_sinistro: "", medico_legale: "", ragione_sociale: "",
  cellulari_extra: [], cellulari_etichette: [], tel_ufficio: "", fax: "",
  tipo_veicolo: "", telaio: "", massimale: "", data_inizio_polizza: "", agenzia_broker: "", num_sinistro_compagnia: "",
};
const TIPI_VEICOLO = ["Autovettura", "Motociclo", "Ciclomotore", "Autocarro", "Autobus", "Rimorchio", "Altro"];
const splitISO = (s) => { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s || "")); return m ? [m[3], m[2], m[1]] : null; };

const pad2 = (n) => String(n).padStart(2, "0");
const tripleToISO = (g, m, a) => (g && m && a ? `${a}-${pad2(m)}-${pad2(g)}` : "");
const join = (arr) => arr.filter(Boolean).join(" ");

function parseAnyDate(s) {
  if (!s) return null;
  const str = String(s).trim();
  let y, m, d;
  if (str.includes("/")) { const [a, b, c] = str.split("/"); d = +a; m = +b; y = +c; }
  else if (str.includes("-")) { const [a, b, c] = str.split("-"); y = +a; m = +b; d = +c; }
  if (!y || !m || !d) return null;
  const dt = new Date(y, m - 1, d);
  return isNaN(dt.getTime()) ? null : dt;
}

function FieldText({ label, value, onChange, testId, mono }) {
  return (
    <div>
      <Label className={LBL}>{label}</Label>
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} className={`${INPUT_CLS} ${mono ? "font-mono" : ""}`} />
    </div>
  );
}
function FieldSelect({ label, value, onChange, options, placeholder, testId }) {
  return (
    <div>
      <Label className={LBL}>{label}</Label>
      <Select value={value || undefined} onValueChange={onChange}>
        <SelectTrigger data-testid={testId} className={`${INPUT_CLS} text-slate-100`}><SelectValue placeholder={placeholder || "Seleziona"} /></SelectTrigger>
        <SelectContent className="bg-[#121824] border-slate-700 text-slate-100">
          {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
function DateField({ label, value, onChange, testId }) {
  return (
    <div>
      <Label className={LBL}>{label}</Label>
      <div className="mt-1.5"><DateInput value={value} onChange={onChange} testId={testId} className="h-9 bg-slate-900/60 border-slate-700" /></div>
    </div>
  );
}
function FieldAuto({ label, value, onChange, source, extraParams, onItem, testId }) {
  return (
    <div>
      <Label className={LBL}>{label}</Label>
      <div className="mt-1.5"><AutocompleteInput value={value} onChange={onChange} source={source} extraParams={extraParams} onSelectItem={onItem} testId={testId} /></div>
    </div>
  );
}

export default function ControparteDialog({ open, onOpenChange, initial, onSave, dataSinistro, conducenteEnabled = false, conducenteValue }) {
  const [form, setForm] = useState(EMPTY);
  const [cond, setCond] = useState({});
  const [saving, setSaving] = useState(false);
  const [cfTouched, setCfTouched] = useState(false);
  const [cfBlur, setCfBlur] = useState(false);
  const isEdit = !!initial;

  useEffect(() => {
    if (open) {
      const init = { ...EMPTY, ...(initial || {}) };
      if (!init.cognome && !init.nome && init.ragione_sociale) init.cognome = init.ragione_sociale;
      if (!init.costruttore && init.marca) init.costruttore = init.marca;
      if (!init.assicurazione && init.compagnia) init.assicurazione = init.compagnia;
      if (/^\s*nessun[aoe]?\s*$/i.test(init.assicurazione || "")) init.assicurazione = "";
      const ve = init.veicolo || (Array.isArray(init.veicoli) && init.veicoli[0]) || {};
      const nd = !init.nasc_g && splitISO(init.data_nascita);
      if (nd) [init.nasc_g, init.nasc_m, init.nasc_a] = nd;
      if (!init.comune_nascita) init.comune_nascita = init.luogo_nascita || "";
      if (!init.comune_nascita_prov) init.comune_nascita_prov = init.provincia_nascita || "";
      if (!init.telefono) init.telefono = init.cellulare || "";
      if (!init.polizza) init.polizza = init.numero_polizza || ve.numero_polizza || "";
      if (!init.assicurazione) init.assicurazione = ve.compagnia || "";
      const sd = !init.scad_g && splitISO(init.scadenza_polizza || ve.scadenza_polizza);
      if (sd) [init.scad_g, init.scad_m, init.scad_a] = sd;
      const imm = init.data_immatricolazione || ve.anno_immatricolazione || init.anno_immatricolazione || "";
      if (!init.data_immatricolazione && /^\d{4}-\d{2}-\d{2}/.test(imm)) init.data_immatricolazione = imm;
      init.anno_immatricolazione = init.anno_immatricolazione || ve.anno_immatricolazione || "";
      ["targa", "modello", "colore", "tipo_veicolo", "telaio", "massimale", "data_inizio_polizza", "agenzia_broker", "num_sinistro_compagnia"].forEach((k) => { if (!init[k]) init[k] = ve[k] || ""; });
      if (!init.costruttore) init.costruttore = ve.marca || "";
      setForm(init);
      setCond(conducenteValue && typeof conducenteValue === "object" ? conducenteValue : {});
      setSaving(false);
      setCfTouched(!!(init.codice_fiscale || "").trim());
    }
  }, [open, initial, conducenteValue]);

  const set = (k, v) => setForm((f) => withCfSesso(f, k, v));
  const setTriple = (prefix, iso) => {
    if (!iso) { setForm((f) => ({ ...f, [`${prefix}_g`]: "", [`${prefix}_m`]: "", [`${prefix}_a`]: "" })); return; }
    const [a, m, g] = iso.split("-");
    setForm((f) => ({ ...f, [`${prefix}_g`]: g, [`${prefix}_m`]: m, [`${prefix}_a`]: a }));
  };

  const eta = useMemo(() => {
    const nasc = parseAnyDate(`${form.nasc_g}/${form.nasc_m}/${form.nasc_a}`);
    const sx = parseAnyDate(dataSinistro);
    if (!nasc || !sx) return "";
    let a = sx.getFullYear() - nasc.getFullYear();
    const mm = sx.getMonth() - nasc.getMonth();
    if (mm < 0 || (mm === 0 && sx.getDate() < nasc.getDate())) a--;
    return a >= 0 && a < 130 ? String(a) : "";
  }, [form.nasc_g, form.nasc_m, form.nasc_a, dataSinistro]);

  const computeCF = () => {
    try {
      return new CodiceFiscale({
        name: form.nome, surname: form.cognome,
        gender: form.sesso === "Femmina" ? "F" : "M",
        day: +form.nasc_g, month: +form.nasc_m, year: +form.nasc_a,
        birthplace: form.comune_nascita, birthplaceProvincia: form.comune_nascita_prov || undefined,
      }).toString();
    } catch (e) { return null; }
  };
  // Ricalcolo automatico del CF a ogni modifica dei dati anagrafici, finché non si modifica il CF a mano
  useEffect(() => {
    if (cfTouched || form.soggetto_iva === "SI") return;
    if (!form.nome || !form.cognome || !form.sesso || !form.comune_nascita || !form.nasc_g || !form.nasc_m || !form.nasc_a) return;
    const cf = computeCF();
    if (cf) setForm((f) => (f.codice_fiscale === cf ? f : { ...f, codice_fiscale: cf }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.nome, form.cognome, form.sesso, form.comune_nascita, form.comune_nascita_prov, form.nasc_g, form.nasc_m, form.nasc_a, form.soggetto_iva, cfTouched]);
  const generaCF = () => {
    const cf = computeCF();
    if (cf) { set("codice_fiscale", cf); setCfTouched(false); toast.success("Codice fiscale generato"); }
    else toast.error("Impossibile generare il CF: compila cognome, nome, sesso, data e comune di nascita corretti");
  };

  const submit = async (e) => {
    e.preventDefault();
    const ragione_sociale = join([form.cognome, form.nome]).trim();
    if (!ragione_sociale) { toast.error("Inserisci almeno cognome o nome"); return; }
    setSaving(true);
    const scad = tripleToISO(form.scad_g, form.scad_m, form.scad_a);
    const hasVeic = !!(form.targa || form.costruttore || form.modello || form.polizza || form.telaio || form.veicolo);
    const veicolo = hasVeic ? {
      ...(form.veicolo || {}), targa: form.targa || "", marca: form.costruttore || "", modello: form.modello || "", colore: form.colore || "",
      compagnia: form.assicurazione || "", numero_polizza: form.polizza || "", scadenza_polizza: scad,
      anno_immatricolazione: form.data_immatricolazione || form.anno_immatricolazione || "",
      tipo_veicolo: form.tipo_veicolo || "", telaio: form.telaio || "", massimale: form.massimale || "", data_inizio_polizza: form.data_inizio_polizza || "",
      agenzia_broker: form.agenzia_broker || "", num_sinistro_compagnia: form.num_sinistro_compagnia || "",
    } : (form.veicolo || null);
    try {
      await onSave({ ...form, ragione_sociale, marca: form.costruttore, compagnia: form.assicurazione,
        data_nascita: tripleToISO(form.nasc_g, form.nasc_m, form.nasc_a), luogo_nascita: form.comune_nascita || "", provincia_nascita: form.comune_nascita_prov || "",
        cellulare: form.telefono || "", numero_polizza: form.polizza || "", scadenza_polizza: scad, veicolo,
        ...(veicolo && Array.isArray(form.veicoli) && form.veicoli.length ? { veicoli: [veicolo, ...form.veicoli.slice(1)] } : veicolo ? { veicoli: [veicolo] } : {}),
        ...(conducenteEnabled ? { conducente: cond } : {}) });
    }
    finally { setSaving(false); }
  };

  const targaInvalid = form.targa && !isValidTarga(form.targa);
  const isIva = form.soggetto_iva === "SI";
  const showVehicle = !["pedone", "ciclista", "trasportato"].some((k) => (form.ruolo || "").toLowerCase().includes(k));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121824] border-slate-700 text-slate-100 max-w-5xl max-h-[85vh] overflow-y-auto" data-testid="controparte-dialog">
        <DialogHeader>
          <DialogTitle className="font-head flex items-center gap-2 tracking-wide">
            <Users className="h-5 w-5 text-cyan-400" /> {isEdit ? "MODIFICA CONTROPARTE" : "NUOVA CONTROPARTE"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} data-testid="controparte-form">
          <div className="grid md:grid-cols-2 gap-x-8 gap-y-4">
            {/* COLONNA SINISTRA */}
            <div className="space-y-4">
              <div>
                <Label className={LBL}>Soggetto IVA</Label>
                <div className="mt-1.5 flex gap-2" data-testid="cp-soggetto_iva">
                  {["SI", "NO"].map((o) => (
                    <button type="button" key={o} onClick={() => set("soggetto_iva", o)} data-testid={`cp-soggetto_iva-${o}`}
                      className={`h-9 px-4 rounded-md border text-sm font-medium transition-colors ${form.soggetto_iva === o ? "border-cyan-500 bg-cyan-500/15 text-cyan-300" : "border-slate-700 text-slate-300 hover:bg-slate-800"}`}>{o}</button>
                  ))}
                </div>
              </div>
              {isIva ? (
                <SoggettoIvaTabs form={form} set={set} setForm={setForm} testId="cp-iva" />
              ) : (<>
              <div><Label className={LBL}>Titolo</Label><TitoloInput value={form.titolo} onChange={(v) => set("titolo", v)} testId="cp-titolo" className="mt-1" /></div>
              <div>
                <Label className={LBL}>Cognome</Label>
                <Input value={form.cognome || ""} onChange={(e) => set("cognome", e.target.value)} data-testid="cp-cognome" className={`${INPUT_CLS} uppercase`} />
              </div>
              <div>
                <Label className={LBL}>Nome</Label>
                <Input value={form.nome || ""} onChange={(e) => set("nome", e.target.value)} data-testid="cp-nome" className={`${INPUT_CLS} uppercase`} />
              </div>
              {!isIva && (
                <>
                  <FieldSelect label="Sesso" value={form.sesso} onChange={(v) => set("sesso", v)} options={["Maschio", "Femmina"]} testId="cp-sesso" />
                  <DateField label="Data di nascita" value={tripleToISO(form.nasc_g, form.nasc_m, form.nasc_a)} onChange={(iso) => setTriple("nasc", iso)} testId="cp-nasc" />
                  <div>
                    <Label className={LBL}>Età in data Sx</Label>
                    <Input value={eta} readOnly disabled data-testid="cp-eta" className={`${INPUT_CLS} text-slate-400`} />
                  </div>
                  <FieldAuto label="Comune di nascita" value={form.comune_nascita} onChange={(v) => set("comune_nascita", v)} source="comuni" onItem={(it) => set("comune_nascita_prov", it.provincia || "")} testId="cp-comune_nascita" />
                </>
              )}
              <div>
                <Label className={LBL}>{isIva ? "P.IVA" : "Cod. Fisc. / P.IVA"}</Label>
                <div className="mt-1.5 flex items-center gap-2">
                  <Input value={form.codice_fiscale || ""} onChange={(e) => { setCfTouched(true); set("codice_fiscale", e.target.value.toUpperCase()); }} onBlur={() => setCfBlur(true)} data-testid="cp-codice_fiscale" className="h-9 bg-slate-900/60 border-slate-700 font-mono flex-1" />
                  {!isIva && (
                    <Button type="button" onClick={generaCF} data-testid="cp-genera-cf" title="Genera codice fiscale" className="h-9 bg-cyan-600 hover:bg-cyan-700 text-white shrink-0"><Sparkles className="h-4 w-4 mr-1" /> Genera CF</Button>
                  )}
                  {!isIva && (
                    <DecodificaCFButton cf={form.codice_fiscale} testId="cp-decodifica-cf" className="h-9 shrink-0"
                      onDecode={(d) => setForm((prev) => ({ ...prev, sesso: d.sesso === "F" ? "Femmina" : "Maschio", nasc_g: d.nasc_g, nasc_m: d.nasc_m, nasc_a: d.nasc_a, comune_nascita: d.comune_nascita, comune_nascita_prov: d.provincia_nascita, cognome: prev.cognome || d.cognomeCode, nome: prev.nome || d.nomeCode }))} />
                  )}
                </div>
                {!isIva && <ValidationHint result={validateCodiceFiscale(form.codice_fiscale)} show={cfBlur} testId="cp-cf" />}
              </div>
              <FieldText label="Professione" value={form.professione} onChange={(v) => set("professione", v)} testId="cp-professione" />
              <div>
                <Label className={LBL}>Indirizzo</Label>
                <div className="mt-1.5"><AddressAutocomplete value={form.indirizzo} onChange={(v) => set("indirizzo", v)} onPick={(pk) => setForm((f) => ({ ...f, indirizzo: pk.indirizzo, comune: pk.comune, cap: pk.cap, provincia: pk.provincia }))} city={form.comune} prov={form.provincia} testId="cp-indirizzo" /></div>
              </div>
              <FieldText label="N° Civ." value={form.numero_civico} onChange={(v) => set("numero_civico", v)} testId="cp-numero_civico" />
              <FieldAuto label="Comune" value={form.comune} onChange={(v) => set("comune", v)} source="comuni" onItem={(it) => { set("provincia", it.provincia || ""); set("cap", it.cap || ""); }} testId="cp-comune" />
              <FieldText label="CAP" value={form.cap} onChange={(v) => set("cap", v)} testId="cp-cap" />
              <FieldAuto label="Provincia" value={form.provincia} onChange={(v) => set("provincia", v)} source="province" testId="cp-provincia" />
              <div>
                <Label className={LBL}>Telefono / Cellulare</Label>
                <div className="mt-1.5">
                  <MultiCellulare extra={form.cellulari_extra} onExtra={(v) => set("cellulari_extra", v)} labels={form.cellulari_etichette} onLabels={(v) => set("cellulari_etichette", v)} testId="cp-cell">
                    <Input value={form.telefono || ""} onChange={(e) => set("telefono", e.target.value)} data-testid="cp-telefono" className="h-9 bg-slate-900/60 border-slate-700" />
                  </MultiCellulare>
                </div>
                <label className="mt-1 flex items-center gap-1.5 cursor-pointer" data-testid="cp-tel-no-cai-wrap">
                  <input type="checkbox" checked={!!form.tel_no_cai} onChange={(e) => set("tel_no_cai", e.target.checked)} data-testid="cp-tel-no-cai" className="h-3.5 w-3.5 accent-cyan-500" />
                  <span className="text-[10px] text-slate-400">Non riportare nella CAI</span>
                </label>
              </div>
              <FieldText label="Tel. ufficio" value={form.tel_ufficio} onChange={(v) => set("tel_ufficio", v)} testId="cp-tel_ufficio" />
              <FieldText label="Fax" value={form.fax} onChange={(v) => set("fax", v)} testId="cp-fax" />
              <FieldText label="E-mail" value={form.email} onChange={(v) => set("email", v)} testId="cp-email" />
              <FieldText label="PEC" value={form.pec} onChange={(v) => set("pec", v)} testId="cp-pec" />
              </>)}
            </div>

            {/* COLONNA DESTRA */}
            <div className="space-y-4">
              <RuoloSinistroField label="Ruolo Sinistro" value={form.ruolo} onChange={(v) => set("ruolo", v)} testId="cp-ruolo" />
              {conducenteEnabled && <ConducenteFormSection value={cond} onChange={setCond} role={form.ruolo} subjectWord="la controparte" testId="cp-cond" />}
              {isConducenteFerito(form.ruolo) && <LesioniPSCard form={form} set={set} testId="cp-lesioni-ps" />}
              {showVehicle && (
                <>
                  <div>
                    <Label className={LBL}>Assicurazione</Label>
                    <div className="mt-1.5"><CompagniaField value={form.assicurazione} onChange={(v) => set("assicurazione", v)} testId="cp-assicurazione" /></div>
                  </div>
                  <IspettoratoField label="Ispettorato" value={form.ispettorato} onChange={(v) => set("ispettorato", v)} compagnia={form.assicurazione} testId="cp-ispettorato" />
                  <FieldText label="Numero polizza" value={form.polizza} onChange={(v) => set("polizza", v)} testId="cp-polizza" mono />
                  <DateField label="Scadenza polizza" value={tripleToISO(form.scad_g, form.scad_m, form.scad_a)} onChange={(iso) => setTriple("scad", iso)} testId="cp-scad" />
                  <FieldAuto label="Costruttore" value={form.costruttore} onChange={(v) => set("costruttore", v)} source="marche" onItem={() => set("modello", "")} testId="cp-costruttore" />
                  <FieldAuto label="Modello" value={form.modello} onChange={(v) => set("modello", v)} source="modelli" extraParams={{ marca: form.costruttore || "" }} testId="cp-modello" />
                  <div>
                    <Label className={LBL}>Colore veicolo</Label>
                    <div className="mt-1.5"><Combobox value={form.colore} onChange={(v) => set("colore", v)} options={COLORI_VEICOLO} placeholder="Scegli o scrivi un colore" testId="cp-colore" /></div>
                  </div>
                  <div>
                    <Label className={LBL}>Targa</Label>
                    <Input value={form.targa || ""} onChange={(e) => set("targa", e.target.value.toUpperCase())} data-testid="cp-targa" className={`${INPUT_CLS} font-mono ${targaInvalid ? "border-red-500/60" : ""}`} />
                    {targaInvalid && <p className="mt-1 text-[11px] text-red-400">Formato targa non valido (es. AB123CD)</p>}
                  </div>
                  <DateField label="Data immatricolazione" value={form.data_immatricolazione} onChange={(v) => set("data_immatricolazione", v)} testId="cp-data_immatricolazione" />
                  <FieldSelect label="Veicolo estero" value={form.veicolo_estero} onChange={(v) => set("veicolo_estero", v)} options={["SI", "NO"]} testId="cp-veicolo_estero" />
                  <FieldSelect label="Tipo veicolo" value={form.tipo_veicolo} onChange={(v) => set("tipo_veicolo", v)} options={TIPI_VEICOLO} testId="cp-tipo_veicolo" />
                  <FieldText label="Telaio (VIN)" value={form.telaio} onChange={(v) => set("telaio", v.toUpperCase())} testId="cp-telaio" mono />
                  <FieldText label="Massimale (€)" value={form.massimale} onChange={(v) => set("massimale", v)} testId="cp-massimale" mono />
                  <DateField label="Data inizio polizza" value={form.data_inizio_polizza} onChange={(v) => set("data_inizio_polizza", v)} testId="cp-data_inizio_polizza" />
                  <FieldText label="Agenzia / Broker" value={form.agenzia_broker} onChange={(v) => set("agenzia_broker", v)} testId="cp-agenzia_broker" />
                  <FieldText label="N. sinistro compagnia" value={form.num_sinistro_compagnia} onChange={(v) => set("num_sinistro_compagnia", v)} testId="cp-num_sinistro_compagnia" mono />
                </>
              )}
              <div>
                <Label className={LBL}>Tipo documento</Label>
                <div className="mt-1.5"><Combobox value={form.documento} onChange={(v) => set("documento", v)} options={DOC_TIPI} testId="cp-documento" /></div>
              </div>
              <FieldText label="Numero documento" value={form.numero_doc} onChange={(v) => set("numero_doc", v)} testId="cp-numero_doc" />
              <div>
                <Label className={LBL}>Rilasciato da</Label>
                <div className="mt-1.5"><Combobox value={form.rilasciato} onChange={(v) => set("rilasciato", v)} options={DOC_RILASCIATO} testId="cp-rilasciato" /></div>
              </div>
              <FieldText label="Luogo di rilascio" value={form.luogo_rilascio_doc} onChange={(v) => set("luogo_rilascio_doc", v)} testId="cp-luogo_rilascio_doc" />
              <DateField label="Data rilascio" value={form.data_emissione_doc} onChange={(v) => set("data_emissione_doc", v)} testId="cp-data_emissione_doc" />
              <DateField label="Data scadenza" value={form.data_scadenza_doc} onChange={(v) => set("data_scadenza_doc", v)} testId="cp-data_scadenza_doc" />
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button type="submit" disabled={saving} data-testid="controparte-submit" className="bg-cyan-600 hover:bg-cyan-700 text-white font-semibold">
              {saving ? "Salvataggio…" : isEdit ? "Salva modifiche" : "Aggiungi controparte"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
